// Enemy definitions: the data side of the enemy system. Phaser-free so it can be
// imported by encounter generation, UI and unit tests. Every number here is a
// sector-1 baseline; hit points and damage scale with SectorDef.difficulty and
// the spawn level (see enemyPower / computeEnemyStats). Behaviour code lives in
// src/systems/enemies/index.ts.

import { COLORS } from '../config/palette';
import type { Rng } from '../core/rng';
import type { EliteAffix, EnemyId } from '../core/types';

// ───────────────────────────── Definitions ─────────────────────────────

/** Timing of an enemy's main (telegraphed) attack. Seconds unless noted. */
export interface EnemyAttackDef {
  /** Telegraph / wind-up duration. */
  windup: number;
  /** Final part of the wind-up during which the aim is locked (no more tracking). */
  lock: number;
  /** Random cooldown range between attacks. */
  cooldown: readonly [number, number];
  /** Recovery after the attack before the unit moves again. */
  recover: number;
  /** Projectile speed px/s (0 = body attack). */
  projectileSpeed: number;
  /** Projectiles (or strikes) per attack. */
  count: number;
  /** Seconds between projectiles of one burst. */
  interval: number;
}

export interface EnemyDef {
  id: EnemyId;
  name: string;
  /** One-line role description (codex / debug). */
  role: string;
  /** Sector-1 hit points. */
  hp: number;
  /** Move speed px/s (walk for ground units, cruise for flyers). */
  speed: number;
  /** Damage dealt by touching the player. */
  contactDamage: number;
  /** Damage of the main attack (per projectile / per strike). */
  attackDamage: number;
  /** Scrap dropped on death (loot reads EnemyApi.scrapValue). */
  scrap: number;
  flying: boolean;
  /** Physics body size in px (before the elite scale). */
  body: { w: number; h: number };
  /** 0..1 reduction of knockback received. */
  knockbackResist: number;
  /** Relative spawn weight for encounter rolls. */
  weight: number;
  /** First sector index (0-based) whose encounters should include this enemy. */
  minSector: number;
  /** Damage within the poise window (as a fraction of max hp) that staggers the unit and cancels its attack. 0 = never. */
  poise: number;
  /** Body gravity px/s² (ground units; flyers ignore it). */
  gravity: number;
  attack: EnemyAttackDef;
}

export const ENEMIES: Readonly<Record<EnemyId, EnemyDef>> = {
  trooper: {
    id: 'trooper',
    name: 'Trooper',
    role: 'Ground soldier. Holds mid range with line of sight and fires telegraphed three-round bursts.',
    hp: 68,
    speed: 175,
    contactDamage: 11,
    attackDamage: 9,
    scrap: 4,
    flying: false,
    body: { w: 34, h: 64 },
    knockbackResist: 0.1,
    weight: 10,
    minSector: 0,
    poise: 0.45,
    gravity: 2400,
    attack: { windup: 0.48, lock: 0.14, cooldown: [1.5, 2.25], recover: 0.28, projectileSpeed: 660, count: 3, interval: 0.1 },
  },
  hornet: {
    id: 'hornet',
    name: 'Hornet',
    role: 'Flying drone. Circles overhead, pulls back, then dives through a locked point.',
    hp: 42,
    speed: 275,
    contactDamage: 14,
    attackDamage: 15,
    scrap: 3,
    flying: true,
    body: { w: 44, h: 30 },
    knockbackResist: 0,
    weight: 8,
    minSector: 0,
    poise: 0.4,
    gravity: 0,
    attack: { windup: 0.42, lock: 0.2, cooldown: [1.8, 2.85], recover: 0.5, projectileSpeed: 1000, count: 1, interval: 0 },
  },
  lancer: {
    id: 'lancer',
    name: 'Lancer',
    role: 'Sniper. Seeks high perches far from the player and fires a fast needle along a charged laser line.',
    hp: 62,
    speed: 138,
    contactDamage: 9,
    attackDamage: 22,
    scrap: 5,
    flying: false,
    body: { w: 30, h: 74 },
    knockbackResist: 0.15,
    weight: 6,
    minSector: 0,
    poise: 0.4,
    gravity: 2400,
    attack: { windup: 0.95, lock: 0.22, cooldown: [2.3, 3.1], recover: 0.38, projectileSpeed: 1600, count: 1, interval: 0.14 },
  },
  bulwark: {
    id: 'bulwark',
    name: 'Bulwark',
    role: 'Shield unit. Advances behind a frontal barrier and bashes forward, then vents with its shield down.',
    hp: 170,
    speed: 84,
    contactDamage: 17,
    attackDamage: 26,
    scrap: 9,
    flying: false,
    body: { w: 58, h: 90 },
    knockbackResist: 0.85,
    weight: 4,
    minSector: 1,
    poise: 0.3,
    gravity: 2400,
    attack: { windup: 0.58, lock: 0.58, cooldown: [2.25, 3.15], recover: 0.9, projectileSpeed: 0, count: 1, interval: 0 },
  },
};

// ───────────────────────────── Per-type AI tuning ─────────────────────────────

export const TROOPER_AI = {
  /** Distance the trooper tries to keep from the player. */
  preferredRange: 360,
  /** Dead zone around preferredRange where it holds position. */
  rangeSlack: 60,
  /** Max distance at which it will open fire. */
  fireRange: 900,
  /** Turn rate of the tracking aim, rad/s. */
  aimTurnRate: 5.5,
  /** Burst projectile collision radius. */
  boltRadius: 7,
  /** Upward velocity used to hop up to two-tile ledges. */
  hopVelocity: 820,
  /** Highest step (px) it will try to hop onto. */
  maxStep: 100,
  /** Longest drop (px) it will walk off to chase the player. */
  maxDrop: 300,
  /** Seconds after spawning before the first attack. */
  firstShotDelay: [0.6, 1.15] as const,
  /** Per-unit random offset of preferredRange, so a squad spreads out instead of stacking. */
  rangeJitter: 90,
  /** Fraction of the player's velocity it leads its aim by (rolled per burst). */
  lead: [0.3, 0.7] as const,
  /** Seconds between strafe-direction changes while holding range. */
  strafeInterval: [0.45, 1.2] as const,
  /** Seconds without line of sight before it gives up its range and hunts the player. */
  huntAfter: 0.8,
  /** Highest jump (px) it makes to follow the player onto a platform. */
  maxJump: 270,
  jumpCooldown: 1.2,
  /** Chance to hop over an incoming player shot (rolled at most every dodgeCooldown s). */
  dodgeChance: 0.25,
  dodgeCooldown: 1.4,
  /** Distance (px) at which incoming shots are noticed. */
  dodgeSense: 280,
} as const;

export const HORNET_AI = {
  /** Hover radius band around the player. */
  orbitMin: 240,
  orbitMax: 320,
  /** Angular drift of the hover position, rad/s. */
  orbitDrift: 0.42,
  bobAmp: 14,
  bobFreq: 2.6,
  /** Steering acceleration px/s². */
  accel: 1100,
  /** Pull-back speed during the wind-up. */
  pullBackSpeed: 170,
  /** Distance flown past the locked point. */
  overshoot: 150,
  /** Climb speed while recovering. */
  recoverRise: 280,
  /** Stun duration after diving into a wall. */
  bonkStun: 0.6,
  /** Wind-up of a chained follow-up dive. */
  chainWindup: 0.3,
  /** Chance to chain a second dive after a miss (overcharged always chains), and the chain limit. */
  chainChance: 0.25,
  maxChain: 1,
  /** Fraction of the player's velocity the dive leads by (rolled per dive). */
  lead: [0.25, 0.6] as const,
  /** Orbit drift multiplier while in front of the player (it circles round to flank). */
  flankDrift: 1.8,
  buzzInterval: [3.5, 7] as const,
  firstDiveDelay: [0.85, 1.55] as const,
} as const;

export const LANCER_AI = {
  /** Preferred minimum distance from the player. */
  minRange: 520,
  /** Ideal sniping distance. */
  idealRange: 720,
  /** Retreats when the player is closer than this. */
  retreatRange: 300,
  fireRange: 1500,
  aimTurnRate: 3.2,
  needleRadius: 6,
  /** Seconds between perch evaluations. */
  perchInterval: [3.5, 5.5] as const,
  /** Max horizontal / vertical reach of a jet leap. */
  leapReachX: 560,
  leapReachY: 260,
  /** Lead fraction of the needle shot (rolled per shot). */
  lead: [0.45, 0.8] as const,
  /** Chance to leap away (instead of walking) when the player gets inside retreatRange. */
  escapeChance: 0.5,
  /** Seconds without line of sight before it looks for a new perch. */
  repositionAfter: 1.4,
  firstShotDelay: [0.85, 1.4] as const,
} as const;

export const BULWARK_AI = {
  /** Engages the bash when the player is within this horizontal band in front. */
  bashMinRange: 50,
  bashMaxRange: 380,
  /** Max vertical difference for a bash (same floor). */
  bashMaxDy: 90,
  bashDistance: 340,
  bashSpeed: 900,
  bashKnockback: 760,
  /** Seconds the player must stay behind before it turns around. */
  turnDelay: 0.28,
  /** Faster turn when the player vaults over it. */
  vaultTurnDelay: 0.15,
  /** Duration of the turn itself. */
  turnTime: 0.38,
  /** Overcharged: ground shockwave released at the end of a bash. */
  waveSpeed: 560,
  waveDamageMult: 0.6,
  firstBashDelay: [1.0, 1.55] as const,
} as const;

// ───────────────────────────── Elites ─────────────────────────────

export interface EliteAffixDef {
  id: EliteAffix;
  /** Upper-case tag shown above the elite. */
  name: string;
  desc: string;
  /** Outline / aura / label colour. */
  color: number;
}

/** Modifiers every elite gets regardless of affixes. */
export const ELITE = {
  hpMult: 2.2,
  scale: 1.2,
  scrapMult: 3,
  poiseMult: 1.6,
} as const;

export const ELITE_AFFIXES_DEF: Readonly<Record<EliteAffix, EliteAffixDef>> = {
  overcharged: {
    id: 'overcharged',
    name: 'OVERCHARGED',
    desc: '+50% attack rate and one extra projectile per burst.',
    color: 0xd65cff,
  },
  shielded: {
    id: 'shielded',
    name: 'SHIELDED',
    desc: 'A hex barrier worth half its hit points absorbs damage first.',
    color: COLORS.shield,
  },
  volatile: {
    id: 'volatile',
    name: 'VOLATILE',
    desc: 'Its core detonates shortly after death.',
    color: COLORS.threatHot,
  },
  swift: {
    id: 'swift',
    name: 'SWIFT',
    desc: '+45% movement speed.',
    color: 0x4dffd2,
  },
};

export const AFFIX = {
  overcharged: { attackRateMult: 1.5, extraProjectiles: 1 },
  shielded: { armorFraction: 0.5 },
  volatile: { fuseMs: 700, radius: 150, damage: 22, knockback: 650 },
  swift: { speedMult: 1.45 },
} as const;

// ───────────────────────────── Scaling ─────────────────────────────

/** Extra hp / damage per spawn level. */
export const LEVEL_STEP = 0.08;

/** Combined hp/damage multiplier for a sector difficulty and spawn level (level 0 = sector baseline). */
export function enemyPower(difficulty: number, level = 0): number {
  const d = Number.isFinite(difficulty) && difficulty > 0 ? difficulty : 1;
  const l = Number.isFinite(level) ? Math.max(0, level) : 0;
  return d * (1 + LEVEL_STEP * l);
}

export interface EnemyStats {
  maxHp: number;
  /** Elite 'shielded' barrier (0 otherwise). */
  maxArmor: number;
  contactDamage: number;
  attackDamage: number;
  /** Final move speed px/s (swift included; slows are applied at runtime). */
  speed: number;
  scrap: number;
  /** Visual + body scale. */
  scale: number;
  /** Multiplier on attack frequency (cooldowns are divided by it). */
  attackRate: number;
  /** Extra projectiles / strikes per attack. */
  extraShots: number;
  /** Poise threshold as a fraction of max hp (0 = cannot be staggered). */
  poise: number;
  power: number;
}

/** Pure stat computation for one spawn (unit-testable). */
export function computeEnemyStats(
  def: EnemyDef,
  opts: { difficulty: number; level?: number; elite?: readonly EliteAffix[] },
): EnemyStats {
  const power = enemyPower(opts.difficulty, opts.level ?? 0);
  const affixes = opts.elite ?? [];
  const elite = affixes.length > 0;
  const maxHp = Math.max(1, Math.round(def.hp * power * (elite ? ELITE.hpMult : 1)));
  return {
    maxHp,
    maxArmor: affixes.includes('shielded') ? Math.round(maxHp * AFFIX.shielded.armorFraction) : 0,
    contactDamage: Math.max(1, Math.round(def.contactDamage * power)),
    attackDamage: Math.max(1, Math.round(def.attackDamage * power)),
    speed: def.speed * (affixes.includes('swift') ? AFFIX.swift.speedMult : 1),
    scrap: Math.max(1, Math.round(def.scrap * Math.sqrt(power) * (elite ? ELITE.scrapMult : 1))),
    scale: elite ? ELITE.scale : 1,
    attackRate: affixes.includes('overcharged') ? AFFIX.overcharged.attackRateMult : 1,
    extraShots: affixes.includes('overcharged') ? AFFIX.overcharged.extraProjectiles : 0,
    poise: def.poise * (elite ? ELITE.poiseMult : 1),
    power,
  };
}

/** Damage of a volatile elite's death blast for a given power. */
export function volatileBlastDamage(power: number): number {
  return Math.max(1, Math.round(AFFIX.volatile.damage * power));
}

/** Helper for encounter code: pick `count` distinct affixes from the sector's allowed list. */
export function rollEliteAffixes(rng: Rng, allowed: readonly EliteAffix[], count = 1): EliteAffix[] {
  const pool = rng.shuffle(allowed.length > 0 ? allowed : (Object.keys(ELITE_AFFIXES_DEF) as EliteAffix[]));
  return pool.slice(0, Math.max(1, Math.min(count, pool.length)));
}

/** Enemies whose minSector allows them in the given sector (encounter helper). */
export function enemiesForSector(sectorIndex: number): EnemyDef[] {
  return (Object.values(ENEMIES) as EnemyDef[]).filter((d) => d.minSector <= sectorIndex);
}
